import type { AdminRole } from '../types/auth';
import type { AdminUsersListResponse, AdminUserDetail } from '../types/admin-user';
import type {
  TagsResponse,
  TransitionsResponse,
  DevelopmentAreasResponse,
  CreateActivityResponse,
  CreateActivityFormData,
  ActivityDetailsResponse,
  UpdateActivityStatusResponse,
  ActivityStatus,
  ModalActivity,
  ActivityCategory,
} from '../types/activity';
import {
  ScheduledActivityResponse,
  ListScheduledActivitiesResponse,
  CreateScheduledActivityDto,
  UpdateScheduledActivityDto,
  ScheduledActivitiesQueryParams,
} from '../types/scheduledActivity';
import type { PredefinedActivityImage } from '../types/upload';
import type {
  CreateGenerationJobInput,
  PreviewPromptInput,
  GenerationJobListResponse,
  GenerationJobDetailResponse,
  GenerationJobCreateResponse,
  PreviewPromptApiResponse,
  GenerationJobStatus,
} from '../types/generation';
import type {
  Strategy,
  StrategyIcon,
  StrategyFilters,
  StrategiesListResponse,
  StrategyCategory,
  PredefinedImage,
  StrategyFormData,
  StrategyStatus,
} from '../types/strategy';
import type {
  ExpertProfile,
  ExpertProfileFilters,
  ExpertProfileFormData,
  ExpertProfilesListResponse,
  ExpertProfileStatus,
} from '../types/expert-profile';
import type {
  ExpertTip,
  ExpertTipFilters,
  ExpertTipFormData,
  ExpertTipsListResponse,
  ExpertTipStatus,
} from '../types/expert-tip';
import type {
  ExpertTipTopic,
  ExpertTipTopicListResponse,
  ExpertTipTopicFilters,
  ExpertTipTopicFormData,
  ActiveTopic,
} from '../types/expert-tip-topic';
import { authenticatedFetch, formatApiError, getAdminToken, type ApiErrorResponse } from './apiClient';
import type {
  SparkActivityFilters,
  SparkActivitiesListResponse,
  SparkActivityDetail,
  SparkActivityBatch,
  SparkActivityFilterOptions,
  BatchUploadResult,
  UpdateSparkActivityPayload,
  ResolveMismatchPayload,
  CreateSparkActivityPayload,
} from '../types/spark-activity';
import type { PackName } from '../types/pack-name';
import type { EarlyAccessFilters, EarlyAccessListResponse } from '../types/early-access';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

/**
 * Admin login request
 */
export interface AdminLoginRequest {
  email: string;
  password: string;
}

/**
 * Admin login response
 */
export interface AdminLoginResponse {
  success: true;
  data: {
    token: string;
    admin: {
      id: number;
      email: string;
      name: string;
      role: AdminRole;
      expertProfileId: number | null;
    };
  };
  message: string;
  timestamp: string;
}

/**
 * Admin profile response
 */
export interface AdminProfileResponse {
  success: true;
  data: {
    id: number;
    email: string;
    name: string;
    role: AdminRole;
    expertProfileId: number | null;
    createdAt: string;
    updatedAt: string;
  };
  message: string;
  timestamp: string;
}

/**
 * Admin API service
 */
export const adminApi = {
  /**
   * Login admin user
   * @param credentials - Email and password
   * @returns Login response with token and admin data
   * @throws Error if login fails
   */
  async login(credentials: AdminLoginRequest): Promise<AdminLoginResponse> {
    const response = await fetch(`${API_BASE_URL}/admin/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Login failed');
    }

    return data as AdminLoginResponse;
  },

  /**
   * Get current admin profile
   * @param token - JWT access token
   * @returns Admin profile data
   * @throws Error if request fails
   */
  async getProfile(token: string): Promise<AdminProfileResponse> {
    const response = await fetch(`${API_BASE_URL}/admin/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch profile');
    }

    return data as AdminProfileResponse;
  },
};

/**
 * Activity API methods
 */
export const activityApi = {
  /**
   * Get all available tags
   * @returns List of tags for activity categorization
   */
  async getTags(): Promise<TagsResponse> {
    console.log('Fetching tags');
    const response = await authenticatedFetch('/admin/activities/tags');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch tags');
    }

    return data as TagsResponse;
  },

  /**
   * Get all available transitions
   * @returns List of time period transitions
   */
  async getTransitions(): Promise<TransitionsResponse> {
    const response = await authenticatedFetch('/admin/activities/transitions');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch transitions');
    }

    return data as TransitionsResponse;
  },

  /**
   * Get development area tags for the generation form dropdown
   * @returns List of development area tags { id, label, key }
   */
  async getDevelopmentAreas(): Promise<DevelopmentAreasResponse> {
    const response = await authenticatedFetch('/admin/activities/development-areas');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch development areas');
    }

    return data as DevelopmentAreasResponse;
  },

  /**
   * Get all activity categories for the dropdown
   */
  async getCategories(): Promise<{ success: true; data: ActivityCategory[]; message: string; timestamp: string }> {
    const response = await authenticatedFetch('/admin/activities/categories');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch categories');
    }

    return data as { success: true; data: ActivityCategory[]; message: string; timestamp: string };
  },

  /**
   * List activities with pagination, search, and filters
   */
  async listActivities(filters: Partial<import('../types/activity').ActivityFilters>): Promise<import('../types/activity').ActivitiesListResponse> {
    // Build query parameters
    const params = new URLSearchParams();
    
    if (filters.search && filters.search.trim()) {
      params.append('search', filters.search.trim());
    }
    
    if (filters.status) {
      params.append('status', filters.status);
    }
    
    if (filters.playType) {
      params.append('playType', filters.playType);
    }
    
    if (filters.prepNeeded) {
      params.append('prepNeeded', filters.prepNeeded);
    }
    
    if (filters.duration) {
      params.append('duration', filters.duration.toString());
    }
    
    // Handle tag IDs array
    if (filters.tagIds && filters.tagIds.length > 0) {
      params.append('tagIds', filters.tagIds.join(','));
    }

    // Handle transition IDs array
    if (filters.transitionIds && filters.transitionIds.length > 0) {
      params.append('transitionIds', filters.transitionIds.join(','));
    }

    // Handle age group keys array
    if (filters.ageGroupKeys && filters.ageGroupKeys.length > 0) {
      params.append('ageGroupKeys', filters.ageGroupKeys.join(','));
    }

    if (filters.categoryIds && filters.categoryIds.length > 0) {
      params.append('categoryIds', filters.categoryIds.join(','));
    }

    if (filters.isAiGenerated) {
      params.append('isAiGenerated', 'true');
    }

    if (filters.generationJobId) {
      params.append('generationJobId', filters.generationJobId);
    }

    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    
    if (filters.pageSize) {
      params.append('pageSize', filters.pageSize.toString());
    }
    
    if (filters.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    
    if (filters.sortOrder) {
      params.append('sortOrder', filters.sortOrder);
    }

    const queryString = params.toString();
    const url = `/admin/activities${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch activities');
    }

    const apiResponse = data as import('../types/activity').ApiResponse<import('../types/activity').ActivitiesListResponse>;
    return apiResponse.data;
  },

  /**
   * Get lightweight activity list for the schedule modal's "Add Activities" panel.
   */
  async listModalActivities(): Promise<ModalActivity[]> {
    const response = await authenticatedFetch('/admin/activities/modal');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch modal activities');
    }

    return data.data as ModalActivity[];
  },

  /**
   * Create a new activity
   * @param formData - Activity form data
   * @returns Created activity
   */
  async createActivity(formData: CreateActivityFormData): Promise<CreateActivityResponse> {
    // Transform form data to API format
    const payload = {
      title: formData.title,
      description: formData.description,
      documentId: formData.documentId || null,
      predefinedActivityImageId: formData.predefinedActivityImageId || null,
      imageType: formData.imageType || null,
      duration: formData.duration,
      playType: formData.playType,
      prepNeeded: formData.prepNeeded,
      steps: formData.steps.filter(s => s.text.trim() !== ''),
      tips: formData.tips.filter(t => t.text.trim() !== ''),
      whyItWorks: formData.whyItWorks,
      tagIds: formData.tagIds,
      transitionIds: formData.transitionIds,
      ageGroupKeys: formData.ageGroupKeys,
      categoryId: formData.categoryId || null,
    };

    const response = await authenticatedFetch('/admin/activities', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to create activity');
    }

    return data as CreateActivityResponse;
  },

  /**
   * Update an existing activity
   * @param id - Activity ID
   * @param formData - Activity form data
   * @returns Updated activity
   */
  async updateActivity(
    id: string,
    formData: CreateActivityFormData
  ): Promise<ActivityDetailsResponse> {
    // Transform form data to API format
    // Strip client-side IDs from steps and tips before sending to API
    const payload = {
      title: formData.title,
      description: formData.description,
      documentId: formData.documentId || null,
      predefinedActivityImageId: formData.predefinedActivityImageId || null,
      imageType: formData.imageType || null,
      duration: formData.duration,
      playType: formData.playType,
      prepNeeded: formData.prepNeeded,
      steps: formData.steps
        .filter(s => s.text.trim() !== '')
        .map(({ id: _id, ...rest }) => rest),
      tips: formData.tips
        .filter(t => t.text.trim() !== '')
        .map(({ id: _id, ...rest }) => rest),
      whyItWorks: formData.whyItWorks,
      tagIds: formData.tagIds,
      transitionIds: formData.transitionIds,
      ageGroupKeys: formData.ageGroupKeys,
      categoryId: formData.categoryId || null,
    };

    const response = await authenticatedFetch(`/admin/activities/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to update activity');
    }

    return data;
  },

  /**
   * Get a single activity by ID
   * @param id - Activity ID
   * @returns Activity details
   * @throws Error if request fails
   */
  async getActivity(id: string): Promise<ActivityDetailsResponse> {
    const response = await authenticatedFetch(`/admin/activities/${id}`);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch activity');
    }

    return data;
  },

  /**
   * Update activity status
   * @param id - Activity ID
   * @param status - New status (draft, published, archived)
   * @returns Updated activity
   * @throws Error if request fails
   */
  async updateActivityStatus(
    id: string,
    status: ActivityStatus
  ): Promise<UpdateActivityStatusResponse> {
    const response = await authenticatedFetch(`/admin/activities/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to update activity status');
    }

    return data;
  },

  async getPredefinedActivityImages(): Promise<{ data: PredefinedActivityImage[] }> {
    const response = await authenticatedFetch('/admin/activities/predefined-images');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch predefined activity images');
    }

    return data;
  },
};

/**
 * Scheduled Activities API methods
 */
export const scheduledActivityApi = {
  /**
   * List scheduled activities with optional filters
   */
  async listScheduledActivities(
    params?: ScheduledActivitiesQueryParams
  ): Promise<ListScheduledActivitiesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.scheduledDate) {
      queryParams.append('scheduledDate', params.scheduledDate);
    }
    if (params?.year !== undefined) {
      queryParams.append('year', params.year.toString());
    }
    if (params?.month !== undefined) {
      queryParams.append('month', params.month.toString());
    }

    const url = `/admin/scheduled-activities${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await authenticatedFetch(url);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch scheduled activities');
    }

    return data.data;
  },

  /**
   * Get scheduled activities for a specific date
   */
  async getScheduledActivitiesForDate(date: string): Promise<ScheduledActivityResponse[]> {
    const response = await authenticatedFetch(`/admin/scheduled-activities/date/${date}`);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch scheduled activities for date');
    }

    return data.data;
  },

  /**
   * Get a single scheduled activity by ID
   */
  async getScheduledActivity(id: string): Promise<ScheduledActivityResponse> {
    const response = await authenticatedFetch(`/admin/scheduled-activities/${id}`);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch scheduled activity');
    }

    return data.data;
  },

  /**
   * Create a new scheduled activity
   */
  async createScheduledActivity(
    dto: CreateScheduledActivityDto
  ): Promise<ScheduledActivityResponse> {
    const response = await authenticatedFetch(`/admin/scheduled-activities`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to create scheduled activity');
    }

    return data.data;
  },

  /**
   * Update a scheduled activity
   */
  async updateScheduledActivity(
    id: string,
    dto: UpdateScheduledActivityDto
  ): Promise<ScheduledActivityResponse> {
    const response = await authenticatedFetch(`/admin/scheduled-activities/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to update scheduled activity');
    }

    return data.data;
  },

  /**
   * Delete a scheduled activity
   */
  async deleteScheduledActivity(id: string): Promise<void> {
    const response = await authenticatedFetch(`/admin/scheduled-activities/${id}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to delete scheduled activity');
    }
  },
};

/**
 * Activity Generation API methods
 */
export const generationApi = {
  /**
   * Preview the interpolated prompt
   */
  async previewPrompt(input: PreviewPromptInput): Promise<PreviewPromptApiResponse> {
    const response = await authenticatedFetch('/admin/activities/generate/preview-prompt', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to preview prompt');
    }

    return data as PreviewPromptApiResponse;
  },

  /**
   * Submit a new generation job
   */
  async submitJob(input: CreateGenerationJobInput): Promise<GenerationJobCreateResponse> {
    const response = await authenticatedFetch('/admin/activities/generate', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to submit generation job');
    }

    return data as GenerationJobCreateResponse;
  },

  /**
   * List generation jobs with optional status filter
   */
  async listJobs(params?: {
    page?: number;
    pageSize?: number;
    status?: GenerationJobStatus;
  }): Promise<GenerationJobListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params?.status) searchParams.append('status', params.status);

    const queryString = searchParams.toString();
    const url = `/admin/activities/jobs${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch generation jobs');
    }

    return data as GenerationJobListResponse;
  },

  /**
   * Get a single job with its generated activities
   */
  async getJob(jobId: string): Promise<GenerationJobDetailResponse> {
    const response = await authenticatedFetch(`/admin/activities/jobs/${jobId}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch generation job');
    }

    return data as GenerationJobDetailResponse;
  },

  /**
   * Retry a failed job
   */
  async retryJob(jobId: string): Promise<GenerationJobCreateResponse> {
    const response = await authenticatedFetch(`/admin/activities/jobs/${jobId}/retry`, {
      method: 'POST',
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to retry generation job');
    }

    return data as GenerationJobCreateResponse;
  },

  /**
   * Delete a job
   */
  async deleteJob(jobId: string): Promise<void> {
    const response = await authenticatedFetch(`/admin/activities/jobs/${jobId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to delete generation job');
    }
  },

  /**
   * Mark an AI-generated activity as reviewed
   */
  async markActivityReviewed(activityId: number): Promise<void> {
    const response = await authenticatedFetch(
      `/admin/activities/${activityId}/mark-reviewed`,
      { method: 'POST' }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to mark activity as reviewed');
    }
  },
};

// Response types for strategy API (ApiResponse envelope wrappers)
type ApiResponse<T> = { success: boolean; data: T; message: string };
type StrategyDetailsResponse = ApiResponse<Strategy>;
type CreateStrategyResponse = ApiResponse<Strategy>;
type CategoriesResponse = ApiResponse<StrategyCategory[]>;
type PredefinedImagesResponse = ApiResponse<PredefinedImage[]>;
type StrategyIconsResponse = ApiResponse<StrategyIcon[]>;

/**
 * Strategy API methods
 */
export const strategyApi = {
  /**
   * List strategies with pagination and filters
   */
  async listStrategies(filters: Partial<StrategyFilters>): Promise<StrategiesListResponse> {
    const params = new URLSearchParams();

    if (filters.categoryId) {
      params.append('categoryId', filters.categoryId);
    }
    if (filters.status) {
      params.append('status', filters.status);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.pageSize) {
      params.append('pageSize', filters.pageSize.toString());
    }
    if (filters.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    if (filters.sortOrder) {
      params.append('sortOrder', filters.sortOrder);
    }

    const queryString = params.toString();
    const url = `/admin/strategies${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch strategies');
    }

    const apiResponse = data as ApiResponse<StrategiesListResponse>;
    return apiResponse.data;
  },

  /**
   * Get a single strategy by ID
   */
  async getStrategy(id: string): Promise<StrategyDetailsResponse> {
    const response = await authenticatedFetch(`/admin/strategies/${id}`);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch strategy');
    }

    return data;
  },

  /**
   * Create a new strategy
   */
  async createStrategy(formData: StrategyFormData): Promise<CreateStrategyResponse> {
    const payload = {
      title: formData.title,
      subtitle: formData.subtitle,
      description: formData.description,
      tip: formData.tip.trim() || null,
      documentId: formData.documentId || null,
      categoryId: parseInt(formData.categoryId, 10),
      sortOrder: parseInt(formData.sortOrder, 10) || 0,
      iconDocumentId: formData.iconDocumentId,
    };

    const response = await authenticatedFetch('/admin/strategies', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to create strategy');
    }

    return data;
  },

  /**
   * Update an existing strategy
   */
  async updateStrategy(
    id: string,
    formData: StrategyFormData
  ): Promise<StrategyDetailsResponse> {
    const payload = {
      title: formData.title,
      subtitle: formData.subtitle,
      description: formData.description,
      tip: formData.tip.trim() || null,
      documentId: formData.documentId || null,
      categoryId: parseInt(formData.categoryId, 10),
      sortOrder: parseInt(formData.sortOrder, 10) || 0,
      iconDocumentId: formData.iconDocumentId,
    };

    const response = await authenticatedFetch(`/admin/strategies/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to update strategy');
    }

    return data;
  },

  /**
   * Update strategy status
   */
  async updateStrategyStatus(
    id: string,
    status: StrategyStatus
  ): Promise<StrategyDetailsResponse> {
    const response = await authenticatedFetch(`/admin/strategies/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to update strategy status');
    }

    return data;
  },

  /**
   * Get all strategy categories
   */
  async getCategories(): Promise<CategoriesResponse> {
    const response = await authenticatedFetch('/admin/strategies/categories');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch categories');
    }

    return data;
  },

  /**
   * Get all predefined strategy images
   */
  async getPredefinedImages(): Promise<PredefinedImagesResponse> {
    const response = await authenticatedFetch('/admin/strategies/predefined-images');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch predefined images');
    }

    return data;
  },

  /**
   * Get all predefined strategy icons
   */
  async getStrategyIcons(): Promise<StrategyIconsResponse> {
    const response = await authenticatedFetch('/admin/strategies/icons');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch strategy icons');
    }

    return data;
  },
};

// ─── Expert Profile API ─────────────────────────────────────────────────────

type ExpertProfileDetailsResponse = ApiResponse<ExpertProfile>;
type CreateExpertProfileResponse = ApiResponse<ExpertProfile>;
type ActiveExpertProfilesResponse = ApiResponse<ExpertProfile[]>;

export const expertProfileApi = {
  async listExpertProfiles(
    filters: Partial<ExpertProfileFilters>
  ): Promise<ExpertProfilesListResponse> {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const queryString = params.toString();
    const url = `/admin/expert-profiles${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch expert profiles');
    }

    const apiResponse = data as ApiResponse<ExpertProfilesListResponse>;
    return apiResponse.data;
  },

  async getExpertProfile(id: string): Promise<ExpertProfileDetailsResponse> {
    const response = await authenticatedFetch(`/admin/expert-profiles/${id}`);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch expert profile');
    }

    return data;
  },

  async createExpertProfile(
    formData: ExpertProfileFormData
  ): Promise<CreateExpertProfileResponse> {
    const payload = {
      name: formData.name,
      subtitle: formData.subtitle,
      avatarDocumentId: formData.avatarDocumentId || null,
    };

    const response = await authenticatedFetch('/admin/expert-profiles', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to create expert profile');
    }

    return data;
  },

  async updateExpertProfile(
    id: string,
    formData: ExpertProfileFormData
  ): Promise<ExpertProfileDetailsResponse> {
    const payload = {
      name: formData.name,
      subtitle: formData.subtitle,
      avatarDocumentId: formData.avatarDocumentId || null,
    };

    const response = await authenticatedFetch(`/admin/expert-profiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to update expert profile');
    }

    return data;
  },

  async updateExpertProfileStatus(
    id: string,
    status: ExpertProfileStatus
  ): Promise<ExpertProfileDetailsResponse> {
    const response = await authenticatedFetch(`/admin/expert-profiles/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to update expert profile status');
    }

    return data;
  },

  async getActiveProfiles(): Promise<ActiveExpertProfilesResponse> {
    const response = await authenticatedFetch('/admin/expert-profiles/active');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch active expert profiles');
    }

    return data;
  },
};

// ─── Expert Tip API ──────────────────────────────────────────────────────────

type ExpertTipDetailsResponse = ApiResponse<ExpertTip>;
type CreateExpertTipResponse = ApiResponse<ExpertTip>;

export const expertTipApi = {
  async listExpertTips(
    filters: Partial<ExpertTipFilters>
  ): Promise<ExpertTipsListResponse> {
    const params = new URLSearchParams();

    if (filters.expertId) params.append('expertId', filters.expertId);
    if (filters.status) params.append('status', filters.status);
    if (filters.topicIds && filters.topicIds.length > 0) {
      params.append('topicIds', filters.topicIds.join(','));
    }
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const queryString = params.toString();
    const url = `/admin/expert-tips${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch expert tips');
    }

    const apiResponse = data as ApiResponse<ExpertTipsListResponse>;
    return apiResponse.data;
  },

  async getExpertTip(id: string): Promise<ExpertTipDetailsResponse> {
    const response = await authenticatedFetch(`/admin/expert-tips/${id}`);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch expert tip');
    }

    return data;
  },

  async createExpertTip(
    formData: ExpertTipFormData
  ): Promise<CreateExpertTipResponse> {
    const payload = {
      title: formData.title,
      body: formData.body,
      expertId: parseInt(formData.expertId, 10),
      topicId: formData.topicId ? parseInt(formData.topicId, 10) : null,
      sortOrder: formData.sortOrder.trim()
        ? parseInt(formData.sortOrder, 10)
        : null,
      linkResources: formData.linkResources.map((lr, index) => ({
        title: lr.title,
        url: lr.url,
        sortOrder: lr.sortOrder ?? index,
      })),
    };

    const response = await authenticatedFetch('/admin/expert-tips', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to create expert tip');
    }

    return data;
  },

  async updateExpertTip(
    id: string,
    formData: ExpertTipFormData
  ): Promise<ExpertTipDetailsResponse> {
    const payload = {
      title: formData.title,
      body: formData.body,
      expertId: parseInt(formData.expertId, 10),
      topicId: formData.topicId ? parseInt(formData.topicId, 10) : null,
      sortOrder: formData.sortOrder.trim()
        ? parseInt(formData.sortOrder, 10)
        : null,
      linkResources: formData.linkResources.map((lr, index) => ({
        title: lr.title,
        url: lr.url,
        sortOrder: lr.sortOrder ?? index,
      })),
    };

    const response = await authenticatedFetch(`/admin/expert-tips/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to update expert tip');
    }

    return data;
  },

  async updateExpertTipStatus(
    id: string,
    status: ExpertTipStatus
  ): Promise<ExpertTipDetailsResponse> {
    const response = await authenticatedFetch(`/admin/expert-tips/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to update expert tip status');
    }

    return data;
  },
};

// ─── Expert Tip Topic API ────────────────────────────────────────────────────

export const expertTipTopicApi = {
  async listTopics(filters: Partial<ExpertTipTopicFilters>): Promise<ExpertTipTopicListResponse> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters.includeDeleted) params.append('includeDeleted', 'true');

    const queryString = params.toString();
    const url = `/admin/expert-tip-topics${queryString ? `?${queryString}` : ''}`;
    const response = await authenticatedFetch(url);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch topics');
    }
    return (data as ApiResponse<ExpertTipTopicListResponse>).data;
  },

  async getActiveTopics(): Promise<ActiveTopic[]> {
    const response = await authenticatedFetch('/admin/expert-tip-topics/active');
    const data = await response.json();
    if (!response.ok) {
      throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch active topics');
    }
    return (data as ApiResponse<ActiveTopic[]>).data;
  },

  async createTopic(formData: ExpertTipTopicFormData): Promise<ExpertTipTopic> {
    const response = await authenticatedFetch('/admin/expert-tip-topics', {
      method: 'POST',
      body: JSON.stringify({ title: formData.title }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to create topic');
    }
    return (data as ApiResponse<ExpertTipTopic>).data;
  },

  async updateTopic(id: string, formData: ExpertTipTopicFormData): Promise<ExpertTipTopic> {
    const response = await authenticatedFetch(`/admin/expert-tip-topics/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title: formData.title }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update topic');
    }
    return (data as ApiResponse<ExpertTipTopic>).data;
  },

  async updateTopicStatus(id: string, isDeleted: boolean): Promise<ExpertTipTopic> {
    const response = await authenticatedFetch(`/admin/expert-tip-topics/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ isDeleted }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update topic status');
    }
    return (data as ApiResponse<ExpertTipTopic>).data;
  },
};

// ─── Expert Tip Topic Schedule API ──────────────────────────────────────────

import type {
  ExpertTipTopicSchedule,
  ExpertTipTopicScheduleListResponse,
} from '../types/expert-tip-topic-schedule';

export const expertTipTopicScheduleApi = {
  async listSchedules(params: {
    year: number;
    month: number;
  }): Promise<ExpertTipTopicScheduleListResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', params.year.toString());
    queryParams.append('month', params.month.toString());

    const url = `/admin/expert-tip-topic-schedules?${queryParams.toString()}`;
    const response = await authenticatedFetch(url);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch schedules');
    }
    return (data as ApiResponse<ExpertTipTopicScheduleListResponse>).data;
  },

  async createSchedule(dto: { topicId: number; weekStart: string }): Promise<ExpertTipTopicSchedule> {
    const response = await authenticatedFetch('/admin/expert-tip-topic-schedules', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to create schedule');
    }
    return (data as ApiResponse<ExpertTipTopicSchedule>).data;
  },

  async deleteSchedule(id: string): Promise<void> {
    const response = await authenticatedFetch(`/admin/expert-tip-topic-schedules/${id}`, {
      method: 'DELETE',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to delete schedule');
    }
  },
};

// ─── Dashboard API ──────────────────────────────────────────────────────────

import type { DashboardStats } from '../types/dashboard';

type DashboardStatsResponse = ApiResponse<DashboardStats>;

/**
 * Dashboard API methods
 */
export const dashboardApi = {
  /**
   * Get aggregated dashboard statistics
   */
  async getStats(): Promise<DashboardStats> {
    const response = await authenticatedFetch('/admin/dashboard/stats');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch dashboard stats');
    }

    const apiResponse = data as DashboardStatsResponse;
    return apiResponse.data;
  },
};

// ─── Admin User API ──────────────────────────────────────────────────────────

export const adminUserApi = {
  async listAdminUsers(): Promise<AdminUsersListResponse> {
    const response = await authenticatedFetch('/admin/users');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch admin users');
    }

    const apiResponse = data as ApiResponse<AdminUsersListResponse>;
    return apiResponse.data;
  },

  async getAdminUser(id: string): Promise<ApiResponse<AdminUserDetail>> {
    const response = await authenticatedFetch(`/admin/users/${id}`);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch admin user');
    }

    return data;
  },

  async assignProfile(
    userId: string,
    expertProfileId: number | null
  ): Promise<ApiResponse<AdminUserDetail>> {
    const response = await authenticatedFetch(`/admin/users/${userId}/assign-profile`, {
      method: 'PATCH',
      body: JSON.stringify({ expertProfileId }),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to assign profile');
    }

    return data;
  },
};

// ─── Admin Recommendation API ────────────────────────────────────────────────

export interface ChildRecommendationGroup {
  date: string;
  recommendations: {
    id: number;
    activityId: number;
    activityTitle: string;
    activityImageUrl: string | null;
    activityStatus: string;
    activityIsDeleted: boolean;
    isDeleted: boolean;
    deletedAt: string | null;
    createdAt: string;
  }[];
  job: {
    id: string;
    status: string;
    createdAt: string;
    completedAt: string | null;
    generatedCount: number | null;
  } | null;
}

export interface ChildRecommendationsResponse {
  child: { id: number; name: string };
  groups: ChildRecommendationGroup[];
}

export interface DeleteRecommendationsResponse {
  deletedCount: number;
  deletionType: 'hard' | 'soft';
  auditId: number;
  deletedRecords: { id: number; activityId: number; activityTitle: string; createdAt: string }[];
  jobSnapshot: Record<string, unknown> | null;
}

export const adminRecommendationApi = {
  async getChildRecommendations(childId: number): Promise<ChildRecommendationsResponse> {
    const response = await authenticatedFetch(`/admin/recommendations/child/${childId}`);
    const data = await response.json();
    if (!response.ok) {
      throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch recommendations');
    }
    return data.data;
  },

  async deleteRecommendationsByDate(
    childId: number,
    date: string,
    deletionType: 'hard' | 'soft'
  ): Promise<DeleteRecommendationsResponse> {
    const response = await authenticatedFetch(`/admin/recommendations/child/${childId}/date/${date}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deletionType }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to delete recommendations');
    }
    return data.data;
  },
};

// ─── Analytics API ────────────────────────────────────────────────────────────

import type {
  AnalyticsStatsResponse,
  AggregationRun,
} from '../types/analytics';
import { apiRequest } from './apiClient';

export const analyticsApi = {
  async getStats(dateRange: { dateFrom?: string; dateTo?: string }): Promise<AnalyticsStatsResponse> {
    const params = new URLSearchParams();
    if (dateRange.dateFrom) params.set('dateFrom', dateRange.dateFrom);
    if (dateRange.dateTo) params.set('dateTo', dateRange.dateTo);
    const queryString = params.toString();
    const url = queryString ? `/admin/analytics/stats?${queryString}` : '/admin/analytics/stats';
    const result = await apiRequest<{ success: true; data: AnalyticsStatsResponse }>(url);
    return result.data;
  },

  async triggerAggregation(): Promise<{ runId: number; status: string }> {
    const result = await apiRequest<{ success: true; data: { runId: number; status: string } }>(
      '/admin/analytics/aggregate',
      { method: 'POST' }
    );
    return result.data;
  },

  async getRun(runId: number): Promise<AggregationRun> {
    const result = await apiRequest<{ success: true; data: AggregationRun }>(
      `/admin/analytics/runs/${runId}`
    );
    return result.data;
  },

  async getRuns(): Promise<AggregationRun[]> {
    const result = await apiRequest<{ success: true; data: AggregationRun[] }>(
      '/admin/analytics/runs'
    );
    return result.data;
  },
};

// ─── Communication Stage API ─────────────────────────────────────────────────

import type { CommunicationStage, CommunicationStageFormData } from '../types/communication-stage';
import type { Domain, DomainFormData } from '../types/domain';
import type { DomainSkill, DomainSkillFormData } from '../types/domain-skill';
import type { ChildPreference, ChildPreferenceFormData } from '../types/child-preference';
import type { SparkPlay, SparkPlayFormData } from '../types/spark-play';
import type { ActivityOverviewResponse, ActivityOverviewStatus } from '../types/activity-overview';

export const sparkActivityOverviewApi = {
  /**
   * Get the Play Activities overview: status counts, breakdowns and coverage matrix
   */
  async getOverview(status: ActivityOverviewStatus): Promise<ActivityOverviewResponse> {
    const response = await authenticatedFetch(`/admin/spark-activities/overview?status=${status}`);
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch activity overview');
    return (data as { success: true; data: ActivityOverviewResponse }).data;
  },
};

export const communicationStageApi = {
  async listStages(): Promise<CommunicationStage[]> {
    const response = await authenticatedFetch('/admin/communication-stages');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch communication stages');
    return (data as { success: true; data: CommunicationStage[] }).data;
  },

  async updateStage(id: number, formData: CommunicationStageFormData): Promise<CommunicationStage> {
    const response = await authenticatedFetch(`/admin/communication-stages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update communication stage');
    return (data as { success: true; data: CommunicationStage }).data;
  },
};

export const domainApi = {
  async listDomains(): Promise<Domain[]> {
    const response = await authenticatedFetch('/admin/domains');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch domains');
    return (data as { success: true; data: Domain[] }).data;
  },

  async updateDomain(id: number, formData: DomainFormData): Promise<Domain> {
    const response = await authenticatedFetch(`/admin/domains/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update domain');
    return (data as { success: true; data: Domain }).data;
  },
};

export const domainSkillApi = {
  async listSkills(params?: { domainId?: number }): Promise<DomainSkill[]> {
    const query = new URLSearchParams();
    if (params?.domainId) query.set('domainId', String(params.domainId));
    const qs = query.toString();
    const response = await authenticatedFetch(`/admin/domain-skills${qs ? `?${qs}` : ''}`);
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch domain skills');
    return (data as { success: true; data: DomainSkill[] }).data;
  },

  async updateSkill(id: number, formData: DomainSkillFormData): Promise<DomainSkill> {
    const response = await authenticatedFetch(`/admin/domain-skills/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update domain skill');
    return (data as { success: true; data: DomainSkill }).data;
  },
};

export const childPreferenceApi = {
  async listPreferences(): Promise<ChildPreference[]> {
    const response = await authenticatedFetch('/admin/child-preferences');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch child preferences');
    return (data as { success: true; data: ChildPreference[] }).data;
  },

  async updatePreference(id: number, formData: ChildPreferenceFormData): Promise<ChildPreference> {
    const response = await authenticatedFetch(`/admin/child-preferences/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update child preference');
    return (data as { success: true; data: ChildPreference }).data;
  },
};

export const sparkPlayApi = {
  async listPlays(): Promise<SparkPlay[]> {
    const response = await authenticatedFetch('/admin/spark-plays');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch spark plays');
    return (data as { success: true; data: SparkPlay[] }).data;
  },

  async updatePlay(id: number, formData: SparkPlayFormData): Promise<SparkPlay> {
    const response = await authenticatedFetch(`/admin/spark-plays/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update spark play');
    return (data as { success: true; data: SparkPlay }).data;
  },
};

// ─── Early Access API ────────────────────────────────────────────────────────

type EarlyAccessApiResponse = { success: boolean; data: EarlyAccessListResponse; message: string };

export const earlyAccessApi = {
  async listSignups(filters: Partial<EarlyAccessFilters>): Promise<EarlyAccessListResponse> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const queryString = params.toString();
    const url = `/admin/early-access${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch early access signups');
    }

    const apiResponse = data as EarlyAccessApiResponse;
    return apiResponse.data;
  },
};

// ─── Spark Activity API ─────────────────────────────────────────────────────

export const sparkActivityApi = {
  async listActivities(filters: Partial<SparkActivityFilters>): Promise<SparkActivitiesListResponse> {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.domainId) params.set('domainId', String(filters.domainId));
    if (filters.skillId) params.set('skillId', String(filters.skillId));
    params.set('status', filters.status ?? 'all');
    if (filters.batchId) params.set('batchId', String(filters.batchId));
    const qs = params.toString();
    const response = await authenticatedFetch(`/admin/spark-activities${qs ? `?${qs}` : ''}`);
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch spark activities');
    return (data as { success: true; data: SparkActivitiesListResponse }).data;
  },

  async getActivity(id: number): Promise<SparkActivityDetail> {
    const response = await authenticatedFetch(`/admin/spark-activities/${id}`);
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch spark activity');
    return (data as { success: true; data: SparkActivityDetail }).data;
  },

  async updateStatus(id: number, status: string): Promise<void> {
    const response = await authenticatedFetch(`/admin/spark-activities/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update status');
  },

  async uploadCsv(file: File): Promise<BatchUploadResult> {
    const token = getAdminToken();
    if (!token) throw new Error('Not authenticated');
    const formData = new FormData();
    formData.append('file', file);
    const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
    const response = await fetch(`${apiBase}/admin/spark-activities/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to upload CSV');
    return (data as { success: true; data: BatchUploadResult }).data;
  },

  async getBatches(): Promise<SparkActivityBatch[]> {
    const response = await authenticatedFetch('/admin/spark-activities/batches');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch batches');
    return (data as { success: true; data: SparkActivityBatch[] }).data;
  },

  async getFilterOptions(): Promise<SparkActivityFilterOptions> {
    const response = await authenticatedFetch('/admin/spark-activities/filters');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch filter options');
    return (data as { success: true; data: SparkActivityFilterOptions }).data;
  },

  async updateActivity(id: number, payload: UpdateSparkActivityPayload): Promise<SparkActivityDetail> {
    const response = await authenticatedFetch(`/admin/spark-activities/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    const json = await response.json();
    if (!response.ok) throw new Error(formatApiError((json as ApiErrorResponse).error, response.status) || 'Failed to update activity');
    return (json as { success: true; data: SparkActivityDetail }).data;
  },

  async resolveMismatch(activityId: number, data: ResolveMismatchPayload): Promise<void> {
    const response = await authenticatedFetch(`/admin/spark-activities/${activityId}/mismatch/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await response.json();
    if (!response.ok) throw new Error(formatApiError((json as ApiErrorResponse).error, response.status) || 'Failed to resolve mismatch');
  },

  async createActivity(payload: CreateSparkActivityPayload): Promise<{ id: number }> {
    const response = await authenticatedFetch('/admin/spark-activities', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to create activity');
    return (data as { success: true; data: { id: number } }).data;
  },

  async checkKeyAvailability(key: string): Promise<{ available: boolean }> {
    const response = await authenticatedFetch(`/admin/spark-activities/check-key?key=${encodeURIComponent(key)}`);
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to check key');
    return (data as { success: true; data: { available: boolean } }).data;
  },

  async deleteActivity(id: number): Promise<void> {
    const response = await authenticatedFetch(`/admin/spark-activities/${id}`, {
      method: 'DELETE',
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to delete activity');
  },

  async deleteBatch(batchId: number): Promise<{ deletedCount: number }> {
    const response = await authenticatedFetch(`/admin/spark-activities/batch/${batchId}`, {
      method: 'DELETE',
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to delete batch');
    return (data as { success: true; data: { deletedCount: number } }).data;
  },

  async downloadCsv(): Promise<void> {
    const token = getAdminToken();
    if (!token) throw new Error('Not authenticated');

    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
    const response = await fetch(`${baseUrl}/admin/spark-activities/download-csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) throw new Error('Failed to download CSV');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `spark-activities-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

export const packNameApi = {
  async listPackNames(): Promise<PackName[]> {
    const response = await authenticatedFetch('/admin/pack-names');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch pack names');
    return (data as { success: true; data: PackName[] }).data;
  },
};
